// analytics/tabs/ProjectTrendsTab.jsx - Updated with unified filters and reportingApi calls

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';
import ProjectDetailsPage from '../components/project_details.jsx';
import ProjectComparisonChart from '../components/charts/ProjectComparisonChart.jsx';

// Use the unified filter hook and components
import { useTabFilters } from '../hooks/useTabFilters.js';
import { UnifiedFilterPanel } from '../components/filters/UnifiedFilterComponents.jsx';

// EXACT SAME transformation function from dashboard.js - DO NOT MODIFY
function transformProjectData(stats, projectName) {
    // Helper function to normalize inspection results
    const normalizeInspectionResult = (result) => {
        if (!result) return 'Unknown';

        const normalizedResult = result.toString().toLowerCase();

        // Map various possible defective indicators
        if (normalizedResult === 'defective' ||
            normalizedResult === 'defect' ||
            normalizedResult === 'fail' ||
            normalizedResult === 'failed' ||
            normalizedResult === 'bad' ||
            normalizedResult === 'reject' ||
            normalizedResult === 'rejected') {
            return 'Defective';
        }

        // Map various possible normal/good indicators
        if (normalizedResult === 'normal' ||
            normalizedResult === 'good' ||
            normalizedResult === 'pass' ||
            normalizedResult === 'passed' ||
            normalizedResult === 'ok' ||
            normalizedResult === 'accept' ||
            normalizedResult === 'accepted') {
            return 'Normal';
        }

        // If we can't determine the result, return the original value capitalized
        return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
    };

    const transformedData = {
        // Core project info
        project_name: projectName,
        name: projectName,

        // Inspection statistics from MongoDB
        total_inspections: stats.total_inspections || 0,
        defect_count: stats.defect_count || 0,
        normal_count: stats.normal_count || 0,
        defect_rate: stats.defect_rate || 0,

        // Statistics object for backward compatibility
        statistics: {
            total: stats.total_inspections || 0,
            defective: stats.defect_count || 0,
            normal: stats.normal_count || 0
        },

        // Image counts from S3 (updated structure)
        training_images: stats.training_images || 0,
        inspection_images: stats.inspection_images || 0,
        inspected_images: stats.inspection_images || 0, // Alias for compatibility

        // Session statistics
        total_sessions: stats.total_sessions || 0,
        active_sessions: stats.active_sessions || 0,
        completed_sessions: stats.completed_sessions || 0,

        // Timestamps
        last_inspection: stats.last_inspection,
        last_run_at: stats.last_inspection, // Alias for compatibility
        created_at: stats.created_at,
        last_modified: stats.last_modified || stats.created_at,

        // Model status - we'll assume trained if there are training images
        model_trained: stats.model_trained || false,

        // Latest inspection info for recent activity - ENHANCED WITH DEBUGGING
        latest_inspection: stats.recent_inspection_details || stats.latest_inspection ? {
            timestamp: (stats.recent_inspection_details?.inspection_time || stats.latest_inspection?.timestamp || stats.latest_inspection?.inspection_time),
            result: normalizeInspectionResult(stats.recent_inspection_details?.result || stats.latest_inspection?.result),
            lot_identifier: (stats.recent_inspection_details?.lot_identifier || stats.latest_inspection?.lot_identifier),
            station_id: (stats.recent_inspection_details?.station_id || stats.latest_inspection?.station_id)
        } : null,

        // Latest image from inspection images directory - Updated URL for FastAPI
        latest_image: stats.latest_image || 'http://localhost:5000/assets/no-training-image.png',

        // Error handling
        error: stats.error || null
    };

    return transformedData;
}

// Enhanced ProjectCard with configuration awareness
const ProjectCard = ({ project, onViewDetails, showConfidence = true }) => {
    const {
        project_name,
        description,
        statistics,
        model_trained,
        latest_image,
        created_at,
        last_modified,
        training_images,
        total_sessions,
        active_sessions,
        latest_inspection
    } = project;

    const stats = statistics || {};
    const defectRate = stats.total > 0
        ? ((stats.defective / stats.total) * 100).toFixed(1)
        : "0.0";

    const modelStatus = {
        trained: model_trained,
        badge: model_trained ? 'bg-success' : 'bg-warning',
        text: model_trained ? 'Model Trained' : 'No Model',
        icon: model_trained ? 'fa-check-circle' : 'fa-brain',
    };

    const getDefectRateColor = () => {
        const rate = parseFloat(defectRate);
        if (rate > 10) return 'text-danger';
        if (rate > 5) return 'text-warning';
        return 'text-success';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const handleImageError = (e) => {
        e.target.src = 'http://localhost:5000/assets/no-training-image.png';
    };

    // Use the same recent activity logic as dashboard
    const createRecentActivity = () => {
        if (!latest_inspection) return null;
        const lotInfo = latest_inspection.lot_identifier ? `Lot: ${latest_inspection.lot_identifier} - ` : '';
        return React.createElement('div', { className: 'alert alert-info py-2 mb-3', key: 'recent-activity' },
            React.createElement('small', null, [
                'Latest: ', lotInfo, formatDate(latest_inspection.timestamp),
                React.createElement('span', {
                    className: `float-end badge ${latest_inspection.result === 'Normal' ? 'bg-success' : 'bg-danger'}`
                }, latest_inspection.result)
            ])
        );
    };

    return (
        <div className="col-md-6 col-lg-4 mb-4">
            <div className="card h-100 shadow-sm border-0">
                <div className="position-relative">
                    <img
                        src={latest_image}
                        className="card-img-top"
                        alt={`${project_name} preview`}
                        style={{ height: '200px', objectFit: 'cover', backgroundColor: '#f8f9fa' }}
                        onError={handleImageError}
                    />
                    <div className="position-absolute top-0 end-0 m-2">
                        <span className={`badge ${modelStatus.badge}`}>
                            <i className={`fas ${modelStatus.icon} me-1`}></i>
                            {modelStatus.text}
                        </span>
                    </div>
                </div>

                <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <h5 className="card-title mb-0">{project_name}</h5>
                        {/* CONDITIONAL: Show confidence badge based on configuration */}
                        {showConfidence && (
                            <span className={`badge ${getDefectRateColor().replace('text-', 'bg-')}`}>
                                {defectRate}%
                            </span>
                        )}
                    </div>

                    <p className="card-text text-muted small flex-grow-1">
                        {description || 'No description available.'}
                    </p>

                    {/* Dates - same as dashboard */}
                    <div className="row mb-3">
                        <div className="col-6">
                            <small className="text-muted">Created</small>
                            <div className="small">{formatDate(created_at)}</div>
                        </div>
                        <div className="col-6">
                            <small className="text-muted">Updated</small>
                            <div className="small">{formatDate(last_modified)}</div>
                        </div>
                    </div>

                    {/* Stats - same layout as dashboard with conditional confidence display */}
                    <div className="mb-3">
                        <h6 className="text-muted mb-2">
                            <i className="fas fa-microscope me-1"></i>
                            Inspection Results
                        </h6>
                        <div className="row text-center">
                            <div className="col-4">
                                <div className="text-primary fw-bold h5 mb-0">{stats.total || 0}</div>
                                <div className="small text-muted">Total</div>
                            </div>
                            <div className="col-4">
                                <div className="text-success fw-bold h5 mb-0">{stats.normal || 0}</div>
                                <div className="small text-muted">Normal</div>
                            </div>
                            <div className="col-4">
                                <div className="text-danger fw-bold h5 mb-0">{stats.defective || 0}</div>
                                <div className="small text-muted">Defective</div>
                            </div>
                        </div>
                        {/* CONDITIONAL: Show defect rate based on configuration */}
                        {showConfidence && (
                            <div className="text-center mt-2">
                                <small className={`fw-bold ${getDefectRateColor()}`}>
                                    Defect Rate: {defectRate}%
                                </small>
                            </div>
                        )}
                    </div>

                    {/* Additional info - same as dashboard */}
                    {(training_images > 0 || total_sessions > 0) && (
                        <div className="mb-3">
                            <div className="row small text-muted">
                                {training_images > 0 && (
                                    <div className="col-6">
                                        <div>Training Images</div>
                                        <div className="fw-bold text-dark">{training_images.toLocaleString()}</div>
                                    </div>
                                )}
                                {total_sessions > 0 && (
                                    <div className="col-6">
                                        <div>Sessions</div>
                                        <div className="fw-bold text-dark">{`${total_sessions} (${active_sessions} active)`}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {createRecentActivity()}

                    <div className="mt-auto d-grid">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => onViewDetails(project)}
                            title="View detailed analysis"
                        >
                            <i className="fas fa-info-circle me-1"></i>View Details
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Simple modal component
const ProjectDetailsModal = ({ project, isOpen, onClose }) => {
    if (!isOpen || !project) return null;

    return (
        <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-fullscreen-lg-down">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-project-diagram me-2"></i>
                            Detailed Analysis: {project.project_name}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <ProjectDetailsPage
                            projectName={project.project_name}
                            onNavigateBack={onClose}
                            onNavigateToProjects={onClose}
                            onNavigateToAnalytics={onClose}
                        />
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ProjectTrendsTab = () => {
    const { state, setError, addNotification } = useAppContext();

    const {
        itemsPerPage,
        show_confidence_scores,
        includeManualImages,
        autoRefresh,
        refreshInterval
    } = state.preferences;

    // Use unified filters with project-specific defaults
    const {
        filters,
        handleFilterChange,
        resetFilters,
        getConfiguredFilters,
        hasInitialized
    } = useTabFilters(
        {
            sortBy: 'defect_rate', // Project-specific default
            statusFilter: ''
        }
    );

    // State for filter dropdown options
    const [availableOptions, setAvailableOptions] = useState({ stations: [] });

    // State for projects
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsProject, setDetailsProject] = useState(null);

    // Load stations for filter options
    useEffect(() => {
        // Use reportingApi for stations
        api.getStations()
            .then(stations => {
                setAvailableOptions(prev => ({ ...prev, stations: stations || [] }));
            })
            .catch(err => console.error("Failed to fetch stations for filters:", err));
    }, []);

    // Load project summaries
    const loadProjectSummaries = useCallback(async () => {
        if (!hasInitialized) return;

        try {
            setIsLoading(true);
            setError(null);
            
            // Get filters from hook (includes dateRange, station, and config preferences like includeManualImages)
            const configuredFilters = getConfiguredFilters();

            // OLD: Direct fetch for basic list
            // const basicResponse = await fetch(`http://localhost:1019/api/v1/projects?page=1&limit=100`, { /* ... */ });
            // const basicData = await basicResponse.json();
            
            // NEW: Use reportingApi.getPaginatedProjects
            // This returns an object like { data: [projects], total: X }
            // const basicData = await api.getPaginatedProjects(1, 100); 
            // const basicProjects = basicData?.data || [];
            const basicProjects = await api.getPaginatedProjects(1, 100); 


            if (!Array.isArray(basicProjects) || basicProjects.length === 0) {
                setProjects([]);
                setIsLoading(false);
                return;
            }

            const projectNames = basicProjects.map(p => p.name);
            
            // OLD: Direct fetch for details
            // const detailsPayload = {
            //     project_names: projectNames,
            //     ...configuredFilters
            // };
            // const detailsResponse = await fetch('http://localhost:1019/api/v1/reports/projects/details', { /* ... */
            //     body: JSON.stringify(detailsPayload)
            // });
            // const detailsData = await detailsResponse.json();

            // NEW: Use reportingApi.getProjectDetails (updated to accept filters)
            // This call returns the map of project data directly.
            const projectDetailsMap = await api.getProjectDetails(projectNames, configuredFilters);
            
            // Original code used detailsData.success/detailsData.data, but now projectDetailsMap 
            // is the map directly, so we check if it's truthy.
            
            const transformedProjects = [];
            if (projectDetailsMap) {
                Object.entries(projectDetailsMap).forEach(([projectName, projectData]) => {
                    const transformedProject = transformProjectData(projectData, projectName);
                    transformedProjects.push(transformedProject);
                });
            }

            setProjects(transformedProjects);

            if (transformedProjects.length === 0 && includeManualImages === false) {
                addNotification({
                    type: 'info',
                    message: 'No projects found. Manual images are excluded - check Configuration tab to include them.',
                    duration: 5000
                });
            }

        } catch (error) {
            setError(error.message);
            addNotification({
                type: 'danger',
                message: `Failed to load projects: ${error.message}`
            });
        } finally {
            setIsLoading(false);
        }
    }, [setError, addNotification, getConfiguredFilters, includeManualImages, hasInitialized]);

    // Load projects when filters change
    useEffect(() => {
        loadProjectSummaries();
    }, [loadProjectSummaries]);

    // Auto-refresh effect
    useEffect(() => {
        if (!autoRefresh || !refreshInterval || !hasInitialized) return;
        const interval = setInterval(loadProjectSummaries, refreshInterval * 1000);
        return () => clearInterval(interval);
    }, [autoRefresh, refreshInterval, loadProjectSummaries, hasInitialized]);

    // Event handlers
    const handleViewDetails = (project) => {
        setDetailsProject(project);
        setShowDetailsModal(true);
    };

    const handleChartProjectClick = (project) => {
        handleViewDetails(project);
    };
    
    // Filtered and sorted projects
    const filteredAndSortedProjects = useMemo(() => {
        if (!projects) return [];
        let filtered = [...projects];

        if (filters.dateRange && filters.dateRange !== 'all') {
            const now = new Date();
            let startDate;
            switch (filters.dateRange) {
                case 'today': startDate = new Date(now.setHours(0, 0, 0, 0)); break;
                case 'week': startDate = new Date(now.setDate(now.getDate() - 7)); break;
                case 'month': startDate = new Date(now.setMonth(now.getMonth() - 1)); break;
                case 'year': startDate = new Date(now.setFullYear(now.getFullYear() - 1)); break;
                default: startDate = null;
            }
            if (startDate) {
                filtered = filtered.filter(p => p.last_inspection && new Date(p.last_inspection) >= startDate);
            }
        }

        if (filters.station) {
            filtered = filtered.filter(p => p.latest_inspection?.station_id === filters.station);
        }

        if (filters.statusFilter) {
            switch (filters.statusFilter) {
                case 'active': filtered = filtered.filter(p => p.active_sessions > 0); break;
                case 'trained': filtered = filtered.filter(p => p.model_trained); break;
                case 'high_defect':
                    filtered = filtered.filter(p => {
                        const rate = p.statistics?.total > 0 ? (p.statistics.defective / p.statistics.total) * 100 : 0;
                        return rate > 5;
                    });
                    break;
            }
        }

        const sorted = filtered.sort((a, b) => {
            switch (filters.sortBy) {
                case 'total_inspections': return (b.statistics?.total || 0) - (a.statistics?.total || 0);
                case 'project_name': return a.project_name.localeCompare(b.project_name);
                case 'last_inspection':
                    const dateA = a.last_inspection ? new Date(a.last_inspection).getTime() : 0;
                    const dateB = b.last_inspection ? new Date(b.last_inspection).getTime() : 0;
                    return dateB - dateA;
                case 'defect_rate':
                default:
                    const rateA = a.statistics?.total > 0 ? (a.statistics.defective / a.statistics.total) : 0;
                    const rateB = b.statistics?.total > 0 ? (b.statistics.defective / b.statistics.total) : 0;
                    return rateB - rateA;
            }
        });

        const displayLimit = itemsPerPage || 50;
        return sorted.slice(0, displayLimit);
    }, [projects, filters, itemsPerPage]);

    const configInfo = {
        itemsPerPage,
        show_confidence_scores,
        includeManualImages,
        autoRefresh,
        refreshInterval
    };

    if (!hasInitialized) {
        return <LoadingSpinner message="Initializing filters..." size="large" />;
    }

    return (
        <ErrorBoundary>
            <div className="project-trends-content fade-in">
                <div className="mb-4">
                    <h4 className="mb-1">Part Trends Analysis</h4>
                    <p className="text-muted mb-0">Analyze and compare performance across different manufactured parts.</p>
                </div>

                <UnifiedFilterPanel
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    availableOptions={availableOptions}
                    config={configInfo}
                    tabType="projects"
                    showConfigInfo={true}
                />

                <div className="card shadow-sm mb-4">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">
                            <i className="fas fa-chart-bar text-primary me-2"></i>
                            Parts Performance Comparison ({filteredAndSortedProjects.length} parts)
                        </h5>
                        <small className="text-muted">
                            Select a bar to see part details •
                            {filteredAndSortedProjects.length < projects.length &&
                                `Limited to ${itemsPerPage || 50} items by configuration`
                            }
                        </small>
                    </div>
                    <div className="card-body" style={{ height: '450px' }}>
                        {isLoading ? (
                            <LoadingSpinner message="Loading chart data..." />
                        ) : (
                            <ProjectComparisonChart
                                projects={filteredAndSortedProjects}
                                onProjectClick={handleChartProjectClick}
                            />
                        )}
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center my-3">
                    <h5 className="mb-0">
                        <i className="fas fa-project-diagram me-2"></i>
                        Parts Overview ({filteredAndSortedProjects.length} parts)
                        {filteredAndSortedProjects.length < projects.length && (
                            <small className="text-muted ms-2">
                                (Limited by configuration - showing top {itemsPerPage || 50})
                            </small>
                        )}
                    </h5>
                </div>

                {isLoading ? (
                    <LoadingSpinner message="Loading projects..." />
                ) : (
                    <div className="row">
                        {filteredAndSortedProjects.length > 0 ? (
                            filteredAndSortedProjects.map(project => (
                                <ProjectCard
                                    key={project.project_name}
                                    project={project}
                                    onViewDetails={handleViewDetails}
                                    showConfidence={show_confidence_scores}
                                />
                            ))
                        ) : (
                            <div className="col-12 text-center p-5 card">
                                <i className="fas fa-folder-open fa-4x text-muted mb-4"></i>
                                <h4 className="text-muted">No Parts Found</h4>
                                <p className="text-muted">No projects match the current filters.</p>
                                {includeManualImages === false && (
                                    <p className="text-info">
                                        <i className="fas fa-info-circle me-1"></i>
                                        Manual images are excluded. Check Configuration tab to include them.
                                    </p>
                                )}
                                <button
                                    className="btn btn-primary"
                                    onClick={resetFilters}
                                    title="Reset all filters to defaults"
                                >
                                    <i className="fas fa-undo me-2"></i>Reset Filters
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <ProjectDetailsModal
                    project={detailsProject}
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setDetailsProject(null);
                    }}
                />
            </div>
        </ErrorBoundary>
    );
};